import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { PushResourcePackage } from "../../resources/PushResourcePackage";
import { IDestroyOptions, Sprite, Text, TextStyle, Texture } from "pixi.js";
import { Button } from "@pixi/ui";
import { GameFont } from "../../../../common/GameFont";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { PushRewardContainer } from "../../components/PushRewardContainer";
import { BasePageView } from "../../../../common/components/BasePageView";
import { PushDialogData } from "../../../../game/flow/impl/dialogAction/PushDialogData";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { VirtualScreenEvent } from "../../../../../../analytics/events/VirtualScreenEvent";

export class MainPage extends BasePageView {
    private static readonly BACKGROUND_1_SCALE = 1.23;
    private static readonly BACKGROUND_2_SCALE = 1.23;

    private readonly pushResources: PushResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly operationControls: PromiseCompletionSource<boolean>;
    private readonly dialogData: PushDialogData;
    private readonly analyticProvider: IAnalyticProviderWrapper;
    private background: Sprite;
    private background2: Sprite;
    private button: Button;
    private buttonText: Text;
    private headerText: Text;
    private declineText: Text;
    private rewardContainer: PushRewardContainer;

    public constructor(operationControls: PromiseCompletionSource<boolean>,
        canvasScaler: ICanvasScaler,
        pushResources: PushResourcePackage,
        commonResources: CommonResourcesPackage,
        dialogData: PushDialogData,
        analyticProvider: IAnalyticProviderWrapper
    ) {
        super(canvasScaler);
        this.pushResources = pushResources;
        this.commonResources = commonResources;
        this.operationControls = operationControls;
        this.dialogData = dialogData;
        this.analyticProvider = analyticProvider;
        this.build();
    }

    protected override build(): void {
        this.background = new Sprite(this.pushResources.assets_views_push_coins_bg_png);
        this.background.scale.set(MainPage.BACKGROUND_1_SCALE);
        this.background.anchor.set(0.5, 1);
        this.addChild(this.background);
        this.background2 = new Sprite(this.pushResources.assets_views_push_coins_bg2_png);
        this.background2.scale.set(MainPage.BACKGROUND_2_SCALE);
        this.background2.anchor.set(0.5, 0);
        this.addChild(this.background2);
        this.declineText = new Text('MAYBE NEXT TIME', new DeclineTextStyle());
        this.declineText.anchor.set(0.5);
        this.declineText.eventMode = "dynamic";
        this.declineText.on("pointerdown", this.onDeclineClick, this);
        this.addChild(this.declineText);
        this.toggleUnderline(this.declineText);
        const goButtonSprite = new Sprite(this.commonResources.assets_common_ui_button_green_png);
        goButtonSprite.anchor.set(0.5, 0.5);
        this.button = new Button(goButtonSprite);
        this.button.view.pivot.set(0.5, 0.5);
        this.button.onPress.connect(this.onApplyClick.bind(this));
        this.buttonText = new Text('LOVE IT!', new ButtonTextStyle());
        this.buttonText.anchor.set(0.5, 0.7);
        this.button.view.addChild(this.buttonText);
        this.addChild(this.button.view);
        this.rewardContainer = new PushRewardContainer(this.dialogData.allowPushNotificationsRewardInCoins, this.commonResources);
        this.addChild(this.rewardContainer);
        this.headerText = new Text('GET UPDATES\nABOUT FREE BONUSES\nIN THE GAME', new HeaderTextStyle());
        this.headerText.anchor.set(0.5);
        this.headerText.skew.set(0, -0.09);
        this.addChild(this.headerText);
        super.build();
    }

    private toggleUnderline(aimText: Text) {
        const underline = new Sprite(Texture.WHITE);
        underline.anchor.set(0.5, 0);
        underline.position.y = aimText.height * 0.5;
        underline.width = aimText.width;
        underline.height = 3;
        aimText.addChild(underline);
    };

    protected override layout(width: number, height: number): void {
        this.background.position.set(width * 0.5, 1906);
        this.background2.position.set(width * 0.5, -47);
        this.declineText.position.set(width * 0.5, height * 0.77);
        this.button.view.position.set(width * 0.5, this.declineText.y - this.declineText.height * 0.5 - this.button.view.height * 0.6);
        this.rewardContainer.position.set(width * 0.5, 876);
        this.headerText.position.set(width * 0.5, 626);
        super.layout(width, height);
    }

    private onApplyClick(): void {
        this.operationControls.complete(true);
    }

    private onDeclineClick(): void {
        this.analyticProvider.registerGoogleEvent(new VirtualScreenEvent("Maybe later Push notifications", this.analyticProvider.currentEventIndex));
        this.operationControls.complete(false);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.button.onPress.disconnectAll();
        this.declineText.off("pointerdown", this.onDeclineClick, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: [
                "#2896fe",
                "#4adef5"
            ],
            fillGradientType: 1,
            fillGradientStops: [
                0.1
            ],
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 96,
            strokeThickness: 1,
            letterSpacing: 0.8,
            lineHeight: 88
        }));
    }
}

class DeclineTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#ffffff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 47
        }));
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            dropShadow: true,
            dropShadowAngle: 0.67,
            dropShadowBlur: 6,
            dropShadowDistance: 2,
            dropShadowAlpha: 0.5,
            fill: "#ffffff",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 80,
            miterLimit: 0
        }));
    }
}
